/**
 * Die Beglaubigung im Browser — Schlüssel, Kodierungen, Ablage.
 *
 * Herausgelöst aus `signing.ts`, damit sich die Rechnung ohne Capacitor und ohne Browser
 * prüfen lässt: alles, was von aussen kommt (`subtle`, die Ablage, das Lesen einer Datei),
 * ist einschiebbar — dieselbe Bauform wie `ServerDeps.fetch` in `server/client.ts`.
 *
 * ⚠️ **Die Kodierungen müssen der nativen Seite auf das Byte gleichen**
 * (`ios/App/App/SigningPlugin.swift`), sonst prüft der Server eine gültige Signatur als
 * ungültig:
 *
 * | Feld | Nativ (CryptoKit) | Hier (WebCrypto) |
 * | --- | --- | --- |
 * | `publicKey` | `rawRepresentation` — `x‖y`, 64 Bytes, **ohne** `0x04` | `exportKey('raw')` gibt 65 Bytes **mit** `0x04`; das erste fällt weg |
 * | `keyId` | SHA-256 über genau diese 64 Bytes, hex | dasselbe |
 * | `signature` | `rawRepresentation` — `r‖s` (IEEE P1363) | `subtle.sign` gibt P1363 unmittelbar |
 */

/** Der Name, unter dem der Schlüssel in der Ablage liegt. */
export const ABLAGE_SCHLUESSEL = 'beglaubigung';

/**
 * Wo das Schlüsselpaar zwischen zwei Besuchen liegt.
 *
 * ⚠️ **IndexedDB und nicht `localStorage`.** Ein `CryptoKey` ist kein Text; nur IndexedDB
 * kann ihn als Objekt aufbewahren, und nur so bleibt der private Teil `extractable: false`.
 * Über `localStorage` müsste er ausgelesen werden — genau das, was nicht sein soll.
 */
export interface Schluesselablage {
  lies(): Promise<CryptoKeyPair | null>;
  schreib(paar: CryptoKeyPair): Promise<void>;
}

const DB_NAME = 'fairexa-beglaubigung';
const SPEICHER = 'schluessel';

function bitte<T>(anfrage: IDBRequest<T>): Promise<T> {
  return new Promise((gelingt, scheitert) => {
    anfrage.onsuccess = () => gelingt(anfrage.result);
    anfrage.onerror = () => scheitert(anfrage.error ?? new Error('IndexedDB lehnte ab.'));
  });
}

function oeffne(): Promise<IDBDatabase> {
  return new Promise((gelingt, scheitert) => {
    const anfrage = indexedDB.open(DB_NAME, 1);
    anfrage.onupgradeneeded = () => {
      if (!anfrage.result.objectStoreNames.contains(SPEICHER)) {
        anfrage.result.createObjectStore(SPEICHER);
      }
    };
    anfrage.onsuccess = () => gelingt(anfrage.result);
    anfrage.onerror = () => scheitert(anfrage.error ?? new Error('IndexedDB lehnte ab.'));
  });
}

/** Die Ablage, die der Browser wirklich benutzt. */
export function indexedDbAblage(): Schluesselablage {
  return {
    async lies() {
      const db = await oeffne();
      try {
        const gelesen = await bitte(
          db.transaction(SPEICHER, 'readonly').objectStore(SPEICHER).get(ABLAGE_SCHLUESSEL),
        );
        return (gelesen as CryptoKeyPair | undefined) ?? null;
      } finally {
        db.close();
      }
    },
    async schreib(paar) {
      const db = await oeffne();
      try {
        await bitte(
          db.transaction(SPEICHER, 'readwrite').objectStore(SPEICHER).put(paar, ABLAGE_SCHLUESSEL),
        );
      } finally {
        db.close();
      }
    },
  };
}

/* ------------------------------------------------------------------- Kodierungen */

export function hex(bytes: Uint8Array): string {
  let aus = '';
  for (const b of bytes) aus += b.toString(16).padStart(2, '0');
  return aus;
}

export function base64(bytes: Uint8Array): string {
  let roh = '';
  // Stückweise: `String.fromCharCode(...bytes)` sprengt bei grossen Feldern den Aufrufstapel.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    roh += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(roh);
}

/**
 * ⚠️ **Der Rückgabetyp nennt `ArrayBuffer` ausdrücklich.** Seit TypeScript 5.7 ist
 * `Uint8Array` über seinen Puffer generisch, und das blosse `Uint8Array` meint
 * `ArrayBufferLike` — das schliesst `SharedArrayBuffer` ein, den `BufferSource` der
 * WebCrypto-Schnittstelle nicht annimmt.
 */
export function ausBase64(text: string): Uint8Array<ArrayBuffer> {
  const roh = atob(text);
  const bytes = new Uint8Array(roh.length);
  for (let i = 0; i < roh.length; i += 1) bytes[i] = roh.charCodeAt(i);
  return bytes;
}

/* ------------------------------------------------------------------- Der Schlüssel */

export const SCHLUESSEL_ART = { name: 'ECDSA', namedCurve: 'P-256' } as const;
export const SIGNIER_ART = { name: 'ECDSA', hash: 'SHA-256' } as const;

/**
 * Holt das Schlüsselpaar aus der Ablage oder legt eines an.
 *
 * ⚠️ **`extractable: false`.** Der private Teil verlässt die Krypto-Maschine des Browsers
 * nicht — auch nicht auf Verlangen der eigenen Seite. Damit ist er so gut verwahrt, wie es im
 * Browser geht; das Profil auf der Platte ist die Grenze, nicht die Seite.
 */
export async function hollSchluessel(
  subtle: SubtleCrypto,
  ablage: Schluesselablage,
): Promise<CryptoKeyPair> {
  const vorhanden = await ablage.lies().catch(() => null);
  if (vorhanden) return vorhanden;

  const paar = (await subtle.generateKey(SCHLUESSEL_ART, false, ['sign', 'verify'])) as CryptoKeyPair;
  // Scheitert das Ablegen, wird trotzdem beglaubigt — dann gilt der Schlüssel eben nur für
  // diese Sitzung. Eine Datei ohne Beglaubigung wäre der schlechtere Ausgang.
  await ablage.schreib(paar).catch(() => undefined);
  return paar;
}

/** Der öffentliche Teil in der Form, die das Manifest verlangt. */
export async function oeffentlicherTeil(
  subtle: SubtleCrypto,
  paar: CryptoKeyPair,
): Promise<{ publicKey: string; keyId: string }> {
  const roh = new Uint8Array(await subtle.exportKey('raw', paar.publicKey));
  // ⚠️ Das führende `0x04` („unkomprimierter Punkt") gehört nicht dazu — CryptoKit lässt es
  // weg, und der Server vergleicht Bytes.
  const xy = roh[0] === 0x04 ? roh.subarray(1) : roh;
  const abdruck = new Uint8Array(await subtle.digest('SHA-256', xy));
  return { publicKey: base64(xy), keyId: hex(abdruck) };
}
